// Lightweight sound + haptics. Sounds are synthesised with the Web Audio API
// (no asset downloads), so they stay tiny and instant on mobile.

import { getSettings } from "./storage";

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  if (!ctx) ctx = new AudioContext();
  // iOS suspends the context until a user gesture; resume opportunistically.
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

type Tone = { freq: number; dur: number; type?: OscillatorType; gain?: number };

function play(tones: Tone[]): void {
  if (!getSettings().sound) return;
  const ac = audio();
  if (!ac) return;
  let t = ac.currentTime;
  for (const tone of tones) {
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = tone.type ?? "sine";
    osc.frequency.value = tone.freq;
    const peak = tone.gain ?? 0.12;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + tone.dur);
    osc.connect(g).connect(ac.destination);
    osc.start(t);
    osc.stop(t + tone.dur);
    t += tone.dur * 0.9;
  }
}

export const sfx = {
  tap: () => play([{ freq: 440, dur: 0.06, type: "triangle", gain: 0.08 }]),
  slide: () => play([{ freq: 320, dur: 0.08, type: "sawtooth", gain: 0.06 }]),
  place: () => play([{ freq: 520, dur: 0.07, type: "triangle", gain: 0.09 }]),
  merge: () => play([{ freq: 600, dur: 0.07 }, { freq: 820, dur: 0.09 }]),
  clear: () => play([{ freq: 660, dur: 0.08 }, { freq: 990, dur: 0.12 }]),
  win: () =>
    play([
      { freq: 523, dur: 0.12 },
      { freq: 659, dur: 0.12 },
      { freq: 784, dur: 0.12 },
      { freq: 1047, dur: 0.2 },
    ]),
  lose: () => play([{ freq: 300, dur: 0.18, type: "sawtooth" }, { freq: 200, dur: 0.22, type: "sawtooth" }]),
  error: () => play([{ freq: 180, dur: 0.12, type: "square", gain: 0.06 }]),
};

export function haptic(pattern: number | number[] = 12): void {
  if (!getSettings().haptics) return;
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    try {
      navigator.vibrate(pattern);
    } catch {
      /* ignore */
    }
  }
}
